"use client";

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStatus } from "@/hooks/useAuthStatus";
import { useDispatch } from "react-redux";
import { logout } from "@/store/slices/auth.slice";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";

export default function InvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { isAuthenticated, adminAuth, statusLoading } = useAuthStatus();
  const [referrerId, setReferrerId] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    const referrer = searchParams.get("referrer") || null;
    setReferrerId(referrer);

    // If no referrer ID, redirect to login
    if (!referrer) {
      navigate("/auth/login?error=invalid-invite");
      return;
    }

    // If user is not authenticated, redirect to login with referrer
    if (!statusLoading && !isAuthenticated) {
      navigate(`/auth/login?referrer=${referrer}`);
      return;
    }
  }, [searchParams, isAuthenticated, statusLoading, navigate]);

  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      // Clear Redux state
      dispatch(logout());

      // Clear client-accessible cookies
      const allCookies = document.cookie.split(";");
      allCookies.forEach((cookie) => {
        const cookieName = cookie.split("=")[0].trim();
        // Clear the cookie by setting expiry to past date
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      });

      // Redirect to login with referrer
      const loginUrl = referrerId
        ? `/auth/login?referrer=${referrerId}`
        : "/auth/login";
      navigate(loginUrl);
    } catch (error) {
      console.error("Error during logout:", error);
      setIsLoggingOut(false);
    }
  };

  const handleContinueWithCurrentAccount = () => {
    // User wants to continue with current account, go to store selector
    navigate("/auth/store-selector");
  };

  // Loading state
  if (statusLoading) {
    return (
      <div className="bg-black w-screen h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-auth-text-muted">Loading invite...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, show logout prompt
  if (isAuthenticated && adminAuth) {
    return (
      <div className="bg-black w-screen h-screen flex items-center justify-center relative overflow-hidden">
        {/* Background stars */}
        <div className="absolute inset-0">
          <div className="absolute top-[10%] left-[20%] w-1 h-1 bg-white rounded-full animate-pulse"></div>
          <div className="absolute top-[15%] right-[25%] w-0.5 h-0.5 bg-white rounded-full opacity-70"></div>
          <div className="absolute top-[25%] left-[10%] w-0.5 h-0.5 bg-white rounded-full opacity-50 animate-pulse delay-500"></div>
          <div className="absolute top-[30%] right-[15%] w-1 h-1 bg-white rounded-full opacity-80"></div>
          <div className="absolute top-[40%] left-[30%] w-0.5 h-0.5 bg-white rounded-full animate-pulse delay-1000"></div>
          <div className="absolute top-[45%] right-[35%] w-0.5 h-0.5 bg-white rounded-full opacity-60"></div>
          <div className="absolute top-[55%] left-[15%] w-1 h-1 bg-white rounded-full opacity-40 animate-pulse delay-700"></div>
          <div className="absolute top-[60%] right-[20%] w-0.5 h-0.5 bg-white rounded-full opacity-80"></div>
          <div className="absolute top-[70%] left-[25%] w-0.5 h-0.5 bg-white rounded-full animate-pulse delay-300"></div>
          <div className="absolute top-[75%] right-[30%] w-1 h-1 bg-white rounded-full opacity-50"></div>
        </div>

        {/* Grid background */}
        <div
          className="absolute bottom-0 left-0 right-0 h-full bg-black"
          style={{
            background: `
            linear-gradient(90deg, transparent 48%, #5659FF88 50%, transparent 52%),
            linear-gradient(0deg, transparent 48%, #5659FF88 50%, transparent 52%)
          `,
            backgroundSize: "100px 60px",
            transform: "perspective(600px) rotateX(70deg)",
            transformOrigin: "bottom",
            maskImage:
              "linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.9) 25%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.2) 75%, transparent 100%), linear-gradient(to right, transparent 0%, rgba(0,0,0,0.3) 10%, rgba(0,0,0,1) 30%, rgba(0,0,0,1) 70%, rgba(0,0,0,0.3) 90%, transparent 100%)",
          }}
        ></div>

        {/* Main content */}
        <div className="relative w-full max-w-md p-[2px] bg-gradient-to-r from-[#5659FF] via-[#8B5FFF] to-[#5659FF] rounded-xl shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-[#5659FF] via-[#8B5FFF] to-[#5659FF] opacity-30 blur-lg rounded-xl"></div>

          <div className="relative bg-gradient-to-br from-auth-card/95 via-auth-surface/95 to-[#0f0f1e]/95 backdrop-blur-sm border border-auth-border/50 rounded-[11px] p-8">
            <div className="text-center mb-8">
              <img
                src="/assets/auth/logo.png"
                alt="Shiphouse Logo"
                width={80}
                height={80}
                className="mx-auto mb-4"
              />
              <h1 className="text-2xl font-semibold text-auth-text mb-2">
                You&apos;re Already Logged In
              </h1>
              <p className="text-auth-text-muted text-sm">
                You&apos;re currently logged in as{" "}
                <span className="text-brand font-medium">
                  {adminAuth.userId}
                </span>
              </p>
              <p className="text-auth-text-muted text-sm mt-2">
                To accept this invite, you need to logout and create a new
                account or login with a different account.
              </p>
            </div>

            <div className="space-y-4">
              <ButtonPrimary
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                {isLoggingOut ? "Logging out..." : "Logout & Accept Invite"}
              </ButtonPrimary>

              <ButtonOutline
                onClick={handleContinueWithCurrentAccount}
                className="w-full border-brand text-brand hover:bg-brand hover:text-primary-foreground"
              >
                Continue with Current Account
              </ButtonOutline>
            </div>

            {referrerId && (
              <div className="mt-6 p-3 bg-brand/10 border border-brand/30 rounded-lg">
                <p className="text-xs text-auth-text-muted text-center">
                  Invite ID:{" "}
                  <span className="text-brand font-mono">{referrerId}</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // This shouldn't normally be reached due to useEffect redirects
  return null;
}
